import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchData } from '../../../reusable'
import moment from 'moment'

// ─── Types ───
interface FilterParams {
  categoryId?: string | number
  fromDate?: string
  toDate?: string
}

interface SelectOption {
  value: string
  label: string
}

export interface SatisfactionSummary {
  overallRating: number
  totalReviews: number
  satisfied: { count: number; percent: number }
  unsatisfied: { count: number; percent: number }
}

export interface DistributionItem {
  star: number
  count: number
  percent: number
}

interface TrendItem {
  month: string
  label: string
  avgRating: number
  reviews: number
}

interface VolumeItem {
  month: string
  label: string
  reviews: number
}

interface ListingDistribution {
  [key: string]: number
}

export interface ListingRating {
  categoryId: number
  listingId: string
  listingName: string
  listingType: string
  rating: number
  reviews: number
  distribution: ListingDistribution
  satisfiedPercent: number
}

const DEFAULT_SUMMARY: SatisfactionSummary = {
  overallRating: 0,
  totalReviews: 0,
  satisfied: { count: 0, percent: 0 },
  unsatisfied: { count: 0, percent: 0 },
}

// ─── Helpers ───
const safeApiCall = async (endpoint: string, params: Record<string, any>): Promise<any> => {
  try {
    const res = await fetchData(endpoint, params)
    if (res?.status == '200' && res?.data) return res.data
    return null
  } catch {
    return null
  }
}

const buildParams = (category: SelectOption | null, dateRange: Date[]): FilterParams => {
  const params: FilterParams = {}
  if (category?.value) params.categoryId = category.value
  if (dateRange?.length === 2) {
    params.fromDate = moment(dateRange[0]).format('DD-MM-YYYY')
    params.toDate = moment(dateRange[1]).format('DD-MM-YYYY')
  }
  return params
}

// ─── Hook ───
export const useSatisfactionRatings = (category: SelectOption | null, dateRange: Date[]) => {
  const [summary, setSummary] = useState<SatisfactionSummary>(DEFAULT_SUMMARY)
  const [distribution, setDistribution] = useState<DistributionItem[]>([])
  const [trend, setTrend] = useState<TrendItem[]>([])
  const [reviewsVolume, setReviewsVolume] = useState<VolumeItem[]>([])
  const [byListing, setByListing] = useState<ListingRating[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  const fetchAll = useCallback(async () => {
    setIsLoading(true)
    const params = buildParams(category, dateRange)

    const [summaryData, distData, trendData, volumeData, listingData] = await Promise.all([
      safeApiCall('admin/satisfactionsummary', params),
      safeApiCall('admin/ratingdistribution', params),
      safeApiCall('admin/ratingtrend', params),
      safeApiCall('admin/reviewsvolume', params),
      safeApiCall('admin/ratingsbylisting', params),
    ])

    if (!isMounted.current) return

    setSummary(summaryData ?? DEFAULT_SUMMARY)
    const distArr = Array.isArray(distData?.distribution) ? distData.distribution : Array.isArray(distData) ? distData : []
    setDistribution(distArr.filter((d: DistributionItem) => d?.star != null))
    setTrend(Array.isArray(trendData) ? trendData : [])
    setReviewsVolume(Array.isArray(volumeData) ? volumeData : [])
    setByListing(Array.isArray(listingData?.list) ? listingData.list : Array.isArray(listingData) ? listingData : [])
    setIsLoading(false)
  }, [category, dateRange])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return { summary, distribution, trend, reviewsVolume, byListing, isLoading }
}
